// Senha temporária: janela que mostra a senha gerada (administrador e Super Admin) e a tela
// "Crie sua senha" que o usuário vê ao entrar com ela.
import { useState, type FormEvent } from 'react';
import { api, mensagemErro } from '../api';
import { useAuth } from '../auth';
import { Marca } from './Marca';
import { Erro, Icon, Modal } from './ui';

export interface SenhaGerada { nome: string; email: string; senhaTemporaria: string; whatsapp?: string | null }

/** Confirmação antes de gerar: derruba o acesso atual do usuário. */
export function ConfirmarSenhaTemporaria({ nome, onClose, onConfirmar }: { nome: string; onClose: () => void; onConfirmar: () => Promise<void> }) {
  const [erro, setErro] = useState<string | null>(null);
  const [gerando, setGerando] = useState(false);
  async function gerar() {
    setErro(null);
    setGerando(true);
    try {
      await onConfirmar();
    } catch (e) {
      setErro(mensagemErro(e));
      setGerando(false);
    }
  }
  return (
    <Modal titulo="Gerar senha temporária" onClose={onClose}>
      <h2 style={{ fontSize: 18 }}>Gerar senha temporária para {nome}?</h2>
      <span className="muted">
        A senha atual deixa de valer e {nome.split(' ')[0]} é desconectado(a) na hora. No primeiro acesso com a senha temporária,
        o sistema pede para criar uma senha nova.
      </span>
      <Erro erro={erro} />
      <div className="modal-foot">
        <button type="button" className="btn" onClick={onClose}>Cancelar</button>
        <button type="button" className="btn primary" disabled={gerando} onClick={gerar}>{gerando ? 'Gerando…' : 'Gerar senha'}</button>
      </div>
    </Modal>
  );
}

/** Mostra a senha uma única vez, com copiar e enviar pelo WhatsApp. */
export function SenhaGeradaModal({ dados, onClose }: { dados: SenhaGerada; onClose: () => void }) {
  const [copiado, setCopiado] = useState(false);
  const endereco = window.location.origin;
  const texto =
    `Olá, ${dados.nome.split(' ')[0]}! Sua senha temporária do Garimpa Vendas é: ${dados.senhaTemporaria}\n` +
    `Entre em ${endereco} com o e-mail ${dados.email}. No primeiro acesso, o sistema pede para você criar uma senha nova.`;
  async function copiar() {
    try {
      await navigator.clipboard.writeText(dados.senhaTemporaria);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      /* navegador sem permissão: a senha está visível para copiar à mão */
    }
  }
  const zap = dados.whatsapp ? `https://wa.me/${dados.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(texto)}` : null;
  return (
    <Modal titulo="Senha temporária" onClose={onClose}>
      <h2 style={{ fontSize: 18 }}>Senha temporária de {dados.nome}</h2>
      <span className="muted small">Entrar com o e-mail <b>{dados.email}</b> e esta senha:</span>
      <div className="senha-gerada">
        <span className="num" aria-label="Senha temporária">{dados.senhaTemporaria}</span>
        <button type="button" className="btn sm" onClick={copiar}>{copiado ? 'Copiada' : 'Copiar'}</button>
      </div>
      <div className="alert warn"><Icon name="alerta" /><span>Ela aparece só agora. Se fechar sem anotar, gere outra.</span></div>
      <div className="modal-foot">
        {zap && (
          <a className="btn" href={zap} target="_blank" rel="noreferrer"><Icon name="whatsapp" size={15} />Enviar pelo WhatsApp</a>
        )}
        <button type="button" className="btn primary" onClick={onClose}>Pronto</button>
      </div>
    </Modal>
  );
}

/** Entrou com senha temporária: substitui o sistema até criar a própria senha. */
export function TelaCriarSenha() {
  const { me, recarregarMe, sair } = useAuth();
  const [f, setF] = useState({ atual: '', nova: '', confirmar: '' });
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  if (!me) return null;
  async function salvar(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    if (f.nova !== f.confirmar) return setErro('A confirmação não confere com a nova senha.');
    if (f.nova === f.atual) return setErro('A nova senha precisa ser diferente da temporária.');
    setSalvando(true);
    try {
      await api('/me/senha', { body: { senhaAtual: f.atual, novaSenha: f.nova } });
      await recarregarMe();
    } catch (err) {
      setErro(mensagemErro(err));
      setSalvando(false);
    }
  }
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="auth-wrap">
      <Marca />
      <form className="auth-card" onSubmit={salvar} style={{ maxWidth: 440 }} aria-labelledby="h-criar-senha">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <h1 id="h-criar-senha" style={{ fontSize: 22 }}>Crie sua senha</h1>
          <span className="muted small">
            Olá, {me.usuario.nome.split(' ')[0]}! Você entrou com uma senha temporária. Crie a sua para continuar.
          </span>
        </div>
        <Erro erro={erro} />
        <label className="field"><span>Senha temporária *</span>
          <input className="input" type="password" required value={f.atual} onChange={set('atual')} autoComplete="current-password" />
        </label>
        <label className="field"><span>Nova senha *</span>
          <input className="input" type="password" required minLength={10} value={f.nova} onChange={set('nova')} autoComplete="new-password" />
          <span className="hint">Mínimo de 10 caracteres.</span>
        </label>
        <label className="field"><span>Confirmar nova senha *</span>
          <input className="input" type="password" required value={f.confirmar} onChange={set('confirmar')} autoComplete="new-password" />
        </label>
        <button className="btn primary lg block" disabled={salvando}>{salvando ? 'Salvando…' : 'Criar senha e entrar'}</button>
        <button type="button" className="btn" onClick={() => sair()}>Sair</button>
      </form>
    </div>
  );
}
